import { action, makeObservable, observable, computed, reaction, runInAction } from "mobx";
import { appRoutes } from "@/app/router";
import { AccountProfileImage } from "@/components/layout/components/account-profile/AccountProfileImage";
import {
  ShareChip,
  ShareSheetChipType,
  ShareSheetEntityKind,
} from "@/components/modal/share-sheet/types";
import { MdsChipInputProps } from "@/design-system/components/input/MdsChipInput";
import {
  SyncModelPermissionEntry,
  SyncModelPermissionEntryWithStatus,
} from "@/domains/sync-scopes/types";
import { isEmail } from "@/domains/validation/validation";
import { copyTextToClipboard } from "@/modules/clipboard/copyTextToClipboard";
import { toastModule } from "@/modules/toast";
import { MdsIcon, MdsIconKind } from "@/design-system/components/icon";
import { Uuid } from "@/domains/global/identifiers";
import { CollectionObservable } from "@/store/collections/CollectionObservable";
import { CollectionIcon } from "@/components/collection/CollectionIcon";
import { INoteObservable } from "@/store/note/types";
import { sortSyncScopePermissionEntries } from "@/domains/sync-scopes";
import { AppStore, GuestAppStore } from "@/store";
import { Maybe } from "@/domains/common/types";
import { ContactObservable } from "@/store/contacts/ContactObservable";
import { MakeCollectionPrivateShareSheetModalStore } from "@/components/modal/share-sheet/make-collection-private-share-sheet-modal-store";
import { isEqual } from "lodash-es";
import { ModalDefinitionKind, ShareSheetModalDefinition } from "@/store/modals/types";
import { ProfileKind, ProfileSize } from "@/components/layout/components/account-profile";
import { windowModule } from "@/modules/window";
import { EventContext } from "@/domains/metrics/context";
import { trackEvent, TrackedEvent } from "@/domains/metrics";
import { TemplateObservable } from "@/store/templates/TemplateObservable";

export type GetCollection = (collectionId: Uuid) => Maybe<CollectionObservable>;
export type GetNote = (noteId: Uuid) => Maybe<INoteObservable>;
export type GetTemplate = (templateId: Uuid) => Maybe<TemplateObservable>;
export type GetMatchingContacts = (query: string) => Promise<ContactObservable[]>;
export type GetMatchingCollections = (query: string) => Promise<CollectionObservable[]>;

enum ORDER_PRIORITY_PREFIX {
  HIGH = "2-",
  MEDIUM = "1-",
  LOW = "0-",
}

export class ShareSheetModalStore {
  private store: AppStore | GuestAppStore;
  private getCollection?: GetCollection;
  private getNote?: GetNote;
  private getTemplate?: GetTemplate;
  private getMatchingContacts?: GetMatchingContacts;
  private getMatchingCollections?: GetMatchingCollections;

  public makeCollectionPrivateModal: MakeCollectionPrivateShareSheetModalStore;

  isGuestMode: boolean;
  id: string | undefined;
  entityKind: ShareSheetEntityKind | undefined;
  chips: ShareChip[] = [];

  isOpen: boolean = false;
  onClose?: () => void;

  isSharing: boolean = false;

  eventContext?: EventContext;

  failedInviteEmails: string[] = [];
  isAwaitingFailureModal: boolean = false;

  private constructor({
    store,
    isGuestMode,
    getCollection,
    getNote,
    getTemplate,
    getMatchingContacts,
    getMatchingCollections,
  }: {
    store: AppStore | GuestAppStore;
    isGuestMode: boolean;
    getCollection?: GetCollection;
    getNote?: GetNote;
    getTemplate?: GetTemplate;
    getMatchingContacts?: GetMatchingContacts;
    getMatchingCollections?: GetMatchingCollections;
  }) {
    this.store = store;
    this.isGuestMode = isGuestMode;
    this.getCollection = getCollection;
    this.getNote = getNote;
    this.getTemplate = getTemplate;
    this.getMatchingContacts = getMatchingContacts;
    this.getMatchingCollections = getMatchingCollections;

    this.makeCollectionPrivateModal = new MakeCollectionPrivateShareSheetModalStore({
      store,
      getCollection,
    });

    makeObservable<
      this,
      | "store"
      | "getCollection"
      | "getNote"
      | "getTemplate"
      | "getMatchingContacts"
      | "getMatchingCollections"
    >(this, {
      template: true,
      store: false,
      getCollection: false,
      getNote: false,
      getTemplate: false,
      getMatchingContacts: false,
      getMatchingCollections: false,
      makeCollectionPrivateModal: false,

      isGuestMode: observable,
      id: observable,
      entityKind: observable,
      chips: observable,
      isOpen: observable,
      onClose: observable,
      isSharing: observable,
      failedInviteEmails: observable,
      isAwaitingFailureModal: observable,
      isUnsharing: computed,
      eventContext: observable,
      failedInviteEmail: computed,

      modalDefinition: computed,
      modal: computed,
      open: action,
      close: action,

      collection: computed,
      note: computed,
      target: computed,
      aclEntries: computed,

      getChips: false,
      setChips: action,
      handleSubmit: action,
      handleGrantAccess: action,
      canUpdateAccess: computed,
      handleUpdateAccess: action,
      canRevokeAccess: computed,
      handleRevokeAccess: action,
      handleCopyCollaborativeLink: action,
      handleCancelOperation: action,
      enqueueFailedInvite: action,
      dequeueFailedInvite: action,
    });

    reaction(
      () => this.modalDefinition,
      (current, previous) => {
        if (!(this.store instanceof AppStore) || isEqual(current, previous)) return;

        if (previous) this.store.modals.removeModal(previous);
        if (current) this.store.modals.addModal(current);
      }
    );

    reaction(
      () => this.target?.canAccess,
      (canAccess, previousCanAccess) => {
        if (!canAccess && canAccess !== previousCanAccess) this.close();
      }
    );
  }

  static forAppStore({ appStore }: { appStore: AppStore }) {
    return new ShareSheetModalStore({
      store: appStore,
      isGuestMode: false,
      getCollection: appStore.collections.get,
      getNote: appStore.notes.get,
      getTemplate: appStore.templates.get,
      getMatchingContacts: appStore.contacts.getMatchingContacts,
      getMatchingCollections: appStore.collections.getMatchingSharedCollections,
    });
  }

  static forGuestStore({ guestStore }: { guestStore: GuestAppStore }) {
    return new ShareSheetModalStore({
      store: guestStore,
      isGuestMode: true,
    });
  }

  get modalDefinition(): Maybe<ShareSheetModalDefinition> {
    if (!this.isOpen) return;

    return {
      kind: ModalDefinitionKind.ShareSheet,
      store: this,
    };
  }

  get modal(): Maybe<ShareSheetModalStore> {
    if (this.store instanceof GuestAppStore) {
      return this.isOpen ? this : undefined;
    }
    // Use modals because there might be another error queued in front of it.
    const store = this.store.modals.shareSheet;
    return store === this ? store : undefined;
  }

  get isUnsharing() {
    if (this.entityKind !== ShareSheetEntityKind.Collection) return false;
    const collectionId = this.collection?.id;
    if (!collectionId) return false;
    const collectionOperations = this.store.sync.actionQueue.operationsByModelId.get(collectionId);
    if (!collectionOperations) return false;
    return collectionOperations.some(
      operation => operation.operationKind === "MAKE_COLLECTION_PRIVATE"
    );
  }

  open = ({
    id,
    entityKind,
    eventContext,
  }: {
    id: Uuid;
    entityKind: ShareSheetEntityKind;
    eventContext: EventContext;
  }) => {
    this.chips = [];
    this.id = id;
    this.entityKind = entityKind;
    this.isOpen = true;
    this.eventContext = eventContext;
    this.failedInviteEmails = [];
    this.isAwaitingFailureModal = false;
  };

  close = () => {
    this.id = undefined;
    this.entityKind = undefined;
    this.onClose?.();
    this.isOpen = false;
    this.eventContext = undefined;
    this.failedInviteEmails = [];
    this.isAwaitingFailureModal = false;
    this.isSharing = false;
  };

  get collection(): Maybe<CollectionObservable> {
    if (this.entityKind !== ShareSheetEntityKind.Collection) return undefined;
    if (!this.id) return undefined;
    return this.getCollection?.(this.id);
  }

  get note(): Maybe<INoteObservable> {
    if (this.entityKind !== ShareSheetEntityKind.Note) return undefined;
    if (!this.id) return undefined;
    return this.getNote?.(this.id);
  }

  get template(): Maybe<TemplateObservable> {
    if (this.entityKind !== ShareSheetEntityKind.Template) return undefined;
    if (!this.id) return undefined;
    return this.getTemplate?.(this.id);
  }

  get target() {
    return this.collection ?? this.note ?? this.template;
  }

  get failedInviteEmail(): string | undefined {
    return this.failedInviteEmails[0];
  }

  get aclEntries(): SyncModelPermissionEntryWithStatus[] {
    if (this.entityKind === ShareSheetEntityKind.Collection)
      return sortSyncScopePermissionEntries(this.store, this.collection?.permissions || []);
    if (this.entityKind === ShareSheetEntityKind.Note)
      return sortSyncScopePermissionEntries(this.store, this.note?.permissions || []);
    if (this.entityKind === ShareSheetEntityKind.Template)
      return sortSyncScopePermissionEntries(this.store, this.template?.permissions || []);
    return [];
  }

  getChips = async (query: string): Promise<ShareChip[]> => {
    const output: ShareChip[] = [];

    const contacts = (await this.getMatchingContacts?.(query)) || [];
    if (!contacts) return output;
    for (const contact of contacts) {
      if (this.target?.grantedSpaceAccountIds.includes(contact.contactSpaceAccountId)) continue;

      output.push({
        id: contact.contactSpaceAccountId,
        type: ShareSheetChipType.SpaceAccount,
        orderByString: `${ORDER_PRIORITY_PREFIX.HIGH}${contact.lastSharedWithAt}`,
        label: contact.profileDisplayName,
        displayName: contact.profileDisplayName,
        secondaryLabel: contact.profileEmailAddress,
        photoUrl: contact.profilePhotoUrl ?? undefined,
        icon: () => (
          <AccountProfileImage
            size={ProfileSize.Standard}
            profile={{
              kind: ProfileKind.Contact,
              data: contact,
            }}
          />
        ),
        searchLabels: [contact.profileDisplayName, contact.profileEmailAddress],
      });
    }

    const email = query.trim();
    if (
      isEmail(email) &&
      !this.target?.grantedEmailAddresses.includes(email) &&
      output.length === 0
    ) {
      output.push({
        id: email,
        type: ShareSheetChipType.Email,
        orderByString: `${ORDER_PRIORITY_PREFIX.LOW}${new Date().toISOString()}`, // Display last
        label: `Invite ${email}`,
        displayName: email,
        icon: (_size?: number) => <MdsIcon kind={MdsIconKind.Envelope} />,
        searchLabels: [email],
      });
    }

    // Early return because we can't share collections with other collections
    if (this.entityKind === ShareSheetEntityKind.Collection) return output;

    const collections = (await this.getMatchingCollections?.(query)) ?? [];
    for (const collection of collections) {
      if (this.target?.grantedCollectionIds.includes(collection.id)) continue;

      output.push({
        id: collection.id,
        type: ShareSheetChipType.Collection,
        orderByString: collection.lastInteractedAtByMe
          ? `${ORDER_PRIORITY_PREFIX.HIGH}${collection.lastInteractedAtByMe}`
          : `${ORDER_PRIORITY_PREFIX.MEDIUM}${collection.lastInteractedAt}`,
        label: collection.label,
        displayName: collection.label,
        secondaryLabel: collection.description, // TODO: Do we need to set max width on this?
        photoUrl: undefined,
        icon: (size?: number) => (
          <CollectionIcon collectionId={collection.id} size={size} legacy={false} />
        ),
        searchLabels: [collection.label],
      });
    }

    return output;
  };

  setChips: MdsChipInputProps["setChips"] = chips => {
    const newChips = (typeof chips === "function" ? chips(this.chips) : chips) as ShareChip[];
    this.chips = newChips.map(chip =>
      chip.type === ShareSheetChipType.Email ? { ...chip, label: chip.displayName } : chip
    );
  };

  enqueueFailedInvite(email: string) {
    if (!email) return;
    const normalized = email.toLowerCase();
    if (this.failedInviteEmails.some(existing => existing.toLowerCase() === normalized)) return;
    this.failedInviteEmails = [...this.failedInviteEmails, email];
  }

  dequeueFailedInvite() {
    if (!this.failedInviteEmails.length) return;
    const [, ...rest] = this.failedInviteEmails;
    this.failedInviteEmails = rest;
  }

  handleSubmit = async () => {
    this.isSharing = true;
    try {
      await Promise.all(this.chips.map(this.handleGrantAccess));

      /**
       * Track the share event
       */
      let eventType: TrackedEvent | undefined = undefined;
      if (this.entityKind === ShareSheetEntityKind.Note) eventType = TrackedEvent.NoteShare;
      if (this.entityKind === ShareSheetEntityKind.Collection)
        eventType = TrackedEvent.CollectionShare;
      if (this.entityKind === ShareSheetEntityKind.Template) eventType = TrackedEvent.TemplateShare;
      if (eventType) {
        trackEvent(eventType, {
          note_id: this.note?.id,
          note_title: this.note?.title,
          collection_id: this.collection?.id,
          collection_title: this.collection?.label,
          num_contacts: this.chips.filter(chip => chip.type === ShareSheetChipType.SpaceAccount)
            .length,
          num_emails: this.chips.filter(chip => chip.type === ShareSheetChipType.Email).length,
          num_collections: this.chips.filter(chip => chip.type === ShareSheetChipType.Collection)
            .length,
          context: this.eventContext ?? EventContext.Unknown,
        });
      }

      runInAction(() => (this.chips = []));
    } finally {
      if (!this.isAwaitingFailureModal) {
        this.isSharing = false;
      }
    }
  };

  handleGrantAccess = async (chip: ShareChip) => {
    switch (chip.type) {
      case ShareSheetChipType.Email: {
        if (this.target?.grantedEmailAddresses.includes(chip.id)) break;

        await this.target?.grantAccessViaEmailAddress({
          targetEmailAddress: chip.id,
          roleKind: "EDITOR",
        });
        break;
      }
      case ShareSheetChipType.SpaceAccount: {
        if (this.target?.grantedSpaceAccountIds.includes(chip.id)) break;

        await this.target?.grantAccessViaSpaceAccount({
          targetSpaceAccountId: chip.id,
          roleKind: "EDITOR",
        });
        break;
      }
      case ShareSheetChipType.Collection: {
        if (this.target?.grantedCollectionIds.includes(chip.id)) break;

        await (this.note || this.template)?.collectionList?.addCollection({
          collectionId: chip.id,
          eventContext: EventContext.ShareModal,
        });
        break;
      }
    }
  };

  get canUpdateAccess() {
    if (this.entityKind === ShareSheetEntityKind.Note) {
      return !!this.note?.updateAccessViaEmailAddress;
    }
    return true;
  }

  handleUpdateAccess = async (permission: SyncModelPermissionEntry) => {
    if (permission.role_kind !== "EDITOR" && permission.role_kind !== "VIEWER") return;
    if (this.entityKind === ShareSheetEntityKind.Note && this.note) {
      if (permission.scope_kind === "EMAIL_SCOPE") {
        await this.note.updateAccessViaEmailAddress({
          targetEmailAddress: permission.email_address,
          roleKind: permission.role_kind,
        });
      }
      if (permission.scope_kind === "SPACE_ACCOUNT_SCOPE") {
        await this.note.updateAccessViaSpaceAccount({
          targetSpaceAccountId: permission.space_account_id,
          roleKind: permission.role_kind,
        });
      }
      if (permission.scope_kind === "COLLECTION_SCOPE") {
        await this.note.updateAccessViaCollection({
          collectionId: permission.collection_id,
          roleKind: permission.role_kind,
        });
      }
      if (permission.scope_kind === "GUEST_ACCOUNT_SCOPE") {
        // TODO: We need a UpdateAccessViaGuestAccount operation.
      }
    }
    if (this.entityKind === ShareSheetEntityKind.Template && this.template) {
      // Not supported.
      // if (permission.scope_kind === "EMAIL_SCOPE") {
      //   await this.template.updateAccessViaEmailAddress({
      //     targetEmailAddress: permission.email_address,
      //     roleKind: permission.role_kind,
      //   });
      // }
      // if (permission.scope_kind === "SPACE_ACCOUNT_SCOPE") {
      //   await this.template.updateAccessViaSpaceAccount({
      //     targetSpaceAccountId: permission.space_account_id,
      //     roleKind: permission.role_kind,
      //   });
      // }
      // if (permission.scope_kind === "COLLECTION_SCOPE") {
      //   await this.template.updateAccessViaCollection({
      //     collectionId: permission.collection_id,
      //     roleKind: permission.role_kind,
      //   });
      // }
      // if (permission.scope_kind === "GUEST_ACCOUNT_SCOPE") {
      //   // TODO: We need a UpdateAccessViaGuestAccount operation.
      // }
    }
    if (this.entityKind === ShareSheetEntityKind.Collection && this.collection) {
      if (permission.scope_kind === "EMAIL_SCOPE") {
        await this.collection.updateAccessViaEmailAddress({
          targetEmailAddress: permission.email_address,
          roleKind: permission.role_kind,
        });
      }
      if (permission.scope_kind === "SPACE_ACCOUNT_SCOPE") {
        await this.collection.updateAccessViaSpaceAccount({
          targetSpaceAccountId: permission.space_account_id,
          roleKind: permission.role_kind,
        });
      }
      if (permission.scope_kind === "GUEST_ACCOUNT_SCOPE") {
        // TODO: We need a UpdateAccessViaGuestAccount operation.
      }
    }
  };

  get canRevokeAccess() {
    // TODO: What is this for?
    if (this.entityKind === ShareSheetEntityKind.Note) {
      return !!this.note?.revokeAccessViaEmailAddress;
    }
    return true;
  }

  handleRevokeAccess = async (permission: SyncModelPermissionEntry) => {
    if (this.entityKind === ShareSheetEntityKind.Note && this.note) {
      if (permission.scope_kind === "EMAIL_SCOPE") {
        await this.note.revokeAccessViaEmailAddress({
          targetEmailAddress: permission.email_address,
        });
      }
      if (permission.scope_kind === "SPACE_ACCOUNT_SCOPE") {
        await this.note.revokeAccessViaSpaceAccount({
          targetSpaceAccountId: permission.space_account_id,
        });
      }
      if (permission.scope_kind === "COLLECTION_SCOPE") {
        await this.note.collectionList?.removeCollection({
          collectionId: permission.collection_id,
        });
      }
      if (permission.scope_kind === "GUEST_ACCOUNT_SCOPE") {
        // TODO: We need a RevokeAccessViaGuestAccount operation.
      }
    }
    if (this.entityKind === ShareSheetEntityKind.Template && this.template) {
      // Not supported.
      // if (permission.scope_kind === "EMAIL_SCOPE") {
      //   await this.template.revokeAccessViaEmailAddress({
      //     targetEmailAddress: permission.email_address,
      //   });
      // }
      if (permission.scope_kind === "SPACE_ACCOUNT_SCOPE") {
        await this.template.revokeAccessViaSpaceAccount({
          targetSpaceAccountId: permission.space_account_id,
        });
      }
      if (permission.scope_kind === "COLLECTION_SCOPE") {
        await this.template.removeFromCollection({
          targetCollectionId: permission.collection_id,
        });
      }
      // Not supported.
      // if (permission.scope_kind === "GUEST_ACCOUNT_SCOPE") {
      //   // TODO: We need a RevokeAccessViaGuestAccount operation.
      // }
    }
    if (this.entityKind === ShareSheetEntityKind.Collection && this.collection) {
      if (permission.scope_kind === "EMAIL_SCOPE") {
        await this.collection.revokeAccessViaEmailAddress({
          targetEmailAddress: permission.email_address,
        });
      }
      if (permission.scope_kind === "SPACE_ACCOUNT_SCOPE") {
        await this.collection.revokeAccessViaSpaceAccount({
          targetSpaceAccountId: permission.space_account_id,
        });
      }
      if (permission.scope_kind === "GUEST_ACCOUNT_SCOPE") {
        // TODO: We need a RevokeAccessViaGuestAccount operation.
      }
    }
  };

  handleCopyCollaborativeLink = async () => {
    if (!this.id || !this.entityKind) return;

    if (this.entityKind === ShareSheetEntityKind.Collection) {
      const targetUrl = windowModule.buildUrl({
        path: appRoutes.collectionsView({ params: { collectionId: this.id } }).path,
      });

      await copyTextToClipboard({
        text: targetUrl.toString(),
      });
    }
    if (this.entityKind === ShareSheetEntityKind.Note) {
      const targetUrl = windowModule.buildUrl({
        path: appRoutes.notesView({ params: { noteId: this.id } }).path,
      });

      await copyTextToClipboard({
        text: targetUrl.toString(),
      });
    }
    if (this.entityKind === ShareSheetEntityKind.Template) {
      const targetUrl = windowModule.buildUrl({
        path: appRoutes.templatesView({ params: { templateId: this.id } }).path,
      });

      await copyTextToClipboard({
        text: targetUrl.toString(),
      });
    }

    toastModule.triggerToast({ content: "Copied collaborative link to clipboard" });
  };

  handleCancelOperation = async (syncOperationId: string) => {
    const success = await this.store.sync.actionQueue.skipAndRevertOperationById(syncOperationId);
    if (success) toastModule.triggerToast({ content: "Cancelled pending access update." });
    else toastModule.triggerToast({ content: "Failed to cancel access update." });
  };
}
